import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, AlertIcon, Box, VStack, useDisclosure, useToast } from "@chakra-ui/react";
import { FaExclamationTriangle } from "react-icons/fa";
import { useLang } from "../../../Hooks";
import { useAuth } from "../../../Contexts/AuthContext";
import { getAvailableReports as fetchAvailableReportsAPI } from "../../../Api/reportApiService";
import reportLevels from "../../../data/reportLevels.json";
import {
  ANALYSIS_TYPES,
  DATA_TYPES,
  formatPeriodName,
  GROUP_BY_OPTIONS,
  ICON_MAP,
  PERIOD_OPTIONS,
  PERIODS,
  TOAST_MESSAGES,
} from "./Constants";
import { validateFilters } from "./AIAnalysis/Helpers/validateFilters";
import FilterCard from "./FilterCard";
import { useExcelParser } from "./Hooks/useExcelParser";
import { useReportExecution } from "./Hooks/useReportExecution";
import { useReportState } from "./Hooks/useReportState";
import { OnExportReport } from "./Helpers/OnExport";
import { getAnalysisAvailability } from "./Helpers/analysisAvailability";
import { getAnalysisReadiness } from "./Helpers/analysisReadiness";
import { getSafeData } from "./Helpers/utils";
import ReportResultsSection from "./ReportResultsSection";

const AdvancedAnalysisPanel = () => {
  const { texts } = useLang();
  const { user } = useAuth();
  const toast = useToast();
  const reportRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const { isOpen: isAnalysisOpen, onToggle: toggleAnalysis } = useDisclosure({ defaultIsOpen: true });
  const isRTL = texts.en === "Arabic";
  const language = isRTL ? "ar" : "en";

  const t = useCallback(
    (key, fallback = "") =>
      key?.split(".").reduce((value, part) => value?.[part], texts) || fallback || key,
    [texts]
  );
  const getIconComponent = useCallback((iconName) => ICON_MAP[iconName], []);

  const state = useReportState({ getIconComponent, isRTL, reportLevels, t });
  const {
    availableReports,
    copied,
    error,
    filters,
    getAdvancedReportInfo,
    getReportList,
    handleApplyFilters,
    handleResetAllFilters,
    handleResetSelection,
    reportsLoading,
    selectedLevel,
    selectedReport,
    setAvailableReports,
    setCopied,
    setError,
    setReportsLoading,
    setSelectedLevel,
    setSelectedReport,
    setShowReport,
    showReport,
  } = state;
  const execution = useReportExecution({
    filters,
    getAdvancedReportInfo,
    getPeriodLabel: (periodValue) => {
      const period = getSafeData(PERIODS, language).find((item) => item.value === periodValue);
      return period?.label || periodValue;
    },
    selectedLevel,
    selectedReport,
    texts,
    toast,
    t,
    user,
  });

  const excel = useExcelParser({
    filters,
    handleApplyFilters,
    setIsLoading: execution.setIsLoading,
    t,
    toast,
  });

  const currentPeriods = useMemo(() => getSafeData(PERIODS, language), [language]);
  const currentPeriodOptions = useMemo(() => getSafeData(PERIOD_OPTIONS, language), [language]);
  const currentGroupByOptions = useMemo(() => getSafeData(GROUP_BY_OPTIONS, language), [language]);
  const currentAnalysisTypes = useMemo(() => getSafeData(ANALYSIS_TYPES, language), [language]);
  const currentDataTypes = useMemo(() => getSafeData(DATA_TYPES, language), [language]);
  const toastMessages = useMemo(() => TOAST_MESSAGES[language] || TOAST_MESSAGES.ar, [language]);
  const requiresAdditionalData = useMemo(() => selectedReport === "general", [selectedReport]);
  const analysisReadiness = useMemo(
    () => getAnalysisReadiness({ filters, language, selectedReport }),
    [filters, language, selectedReport]
  );
  const analysisAvailability = useMemo(
    () => getAnalysisAvailability({ availableReports, language, selectedReport, t }),
    [availableReports, language, selectedReport, t]
  );

  const getPeriodLabel = useCallback(
    (periodValue) => {
      const matchedPeriod = currentPeriods.find((item) => item.value === periodValue);
      if (matchedPeriod) return matchedPeriod.label;
      if (/^\d{4}-\d{2}(-\d{2})?$/.test(periodValue || "")) {
        return formatPeriodName(periodValue, language);
      }
      return periodValue;
    },
    [currentPeriods, language]
  );

  useEffect(() => {
    let ignore = false;

    const fetchAvailableReports = async () => {
      try {
        setReportsLoading(true);
        const response = await fetchAvailableReportsAPI(selectedLevel);
        if (!ignore && response.success) {
          setAvailableReports({
            financialStatements: response.data?.FinancialStatements || [],
            financialRatios: response.data?.FinancialRatios || [],
            aiAnalysis: response.data?.AiAnalysis || [],
          });
        }
      } finally {
        if (!ignore) setReportsLoading(false);
      }
    };

    fetchAvailableReports();
    return () => {
      ignore = true;
    };
  }, [selectedLevel, setAvailableReports, setReportsLoading]);

  const handleRunReport = async () => {
    if (execution.isAnalyzing) {
      await execution.cancelAnalysis();
      return;
    }

    if (!analysisAvailability.isAvailable) {
      setError(analysisAvailability.message);
      toast({
        title: t("reports.analysisUnavailable", language === "en" ? "Analysis unavailable" : "التحليل غير متاح"),
        description: analysisAvailability.message,
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (!analysisReadiness.isReady) {
      setError(analysisReadiness.message);
      toast({
        title: t("reports.analysisRequirements", "شروط التحليل غير مستوفاة"),
        description: analysisReadiness.message,
        status: "warning",
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    const validationError = validateFilters({
      filters,
      requiresAdditionalData,
      t,
    });

    if (validationError) {
      setError(validationError);
      toast({
        title: t("reports.validationError", "خطأ في التحقق"),
        description: validationError,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const result = await execution.runReport({
      analysisAvailability,
      reportRef,
      setCopied,
      setError,
      availableReports,
    });

    if (result) {
      setShowReport(true);
    }
  };

  const handleExport = async (format) => {
    if (isExporting) {
      return;
    }

    try {
      setIsExporting(true);
      await OnExportReport({
        getAdvancedReportInfo,
        reportApiResponse: execution.reportApiResponse,
        selectedReport,
        reportData: execution.reportData,
        aiAnalysis: execution.aiAnalysis,
        filters,
        format,
        isRTL,
        toast,
        t,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box p={0.5} dir={isRTL ? "rtl" : "ltr"} textAlign={isRTL ? "right" : "left"}>
      <VStack spacing={2} align="stretch">
        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon as={FaExclamationTriangle} />
            {error}
          </Alert>
        )}
        <FilterCard
          analysisReadiness={analysisReadiness}
          analysisAvailability={analysisAvailability}
          analysisProgress={execution.analysisProgress}
          availableReports={availableReports}
          currentAnalysisTypes={currentAnalysisTypes}
          currentDataTypes={currentDataTypes}
          currentGroupByOptions={currentGroupByOptions}
          currentPeriodOptions={currentPeriodOptions}
          currentPeriods={currentPeriods}
          excel={excel}
          filters={filters}
          getAdvancedReportInfo={getAdvancedReportInfo}
          getIconComponent={getIconComponent}
          getReportList={getReportList}
          handleApplyFilters={handleApplyFilters}
          handleCancelAnalysis={execution.cancelAnalysis}
          handleReset={() => {
            handleResetSelection();
            execution.resetResults();
          }}
          handleResetAllFilters={handleResetAllFilters}
          handleRunReport={handleRunReport}
          isAnalyzing={execution.isAnalyzing}
          isLoading={execution.isLoading}
          isRTL={isRTL}
          reportLevels={reportLevels}
          reportsLoading={reportsLoading}
          requiresAdditionalData={requiresAdditionalData}
          selectedLevel={selectedLevel}
          selectedReport={selectedReport}
          setError={setError}
          setSelectedLevel={setSelectedLevel}
          setSelectedReport={setSelectedReport}
          showReport={showReport}
          t={t}
          toast={toast}
          toastMessages={toastMessages}
          usageLimitReached={execution.usageLimitReached}
        />
        <ReportResultsSection
          analysis={{ handleExport }}
          copied={copied}
          execution={execution}
          filters={filters}
          getAdvancedReportInfo={getAdvancedReportInfo}
          getPeriodLabel={getPeriodLabel}
          isAnalysisOpen={isAnalysisOpen}
          isRTL={isRTL}
          language={language}
          reportRef={reportRef}
          isExporting={isExporting}
          selectedLevel={selectedLevel}
          selectedReport={selectedReport}
          setCopied={setCopied}
          showReport={showReport}
          t={t}
          texts={texts}
          toast={toast}
          toggleAnalysis={toggleAnalysis}
        />
      </VStack>
    </Box>
  );
};

export default AdvancedAnalysisPanel;
